import { apiClient } from './client';
import type {
  DocumentFolder,
  FolderTreeResponse,
  FolderCreateRequest,
  FolderUpdateRequest,
  DuplicateCheckRequest,
  DuplicateCheckResponse,
  DocumentMoveRequest,
} from '@/types/folder';

// Extraction status types
export type ExtractionStatus = 'pending' | 'processing' | 'staged' | 'completed' | 'failed' | 'skipped';

// Document types matching backend enum
export type DocumentType =
  | 'agmr'
  | 'gw_gradient'
  | 'gw_concentrations'
  | 'om'
  | 'product_recovery'
  | 'lab_report'
  | 'other';

export interface SiteDocument {
  id: string;
  site_id: string;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  document_type: DocumentType;
  document_date: string | null;
  description: string | null;
  created_at: string;
  extraction_status?: ExtractionStatus | null;
}

export interface DocumentListResponse {
  items: SiteDocument[];
  total: number;
  page: number;
  page_size: number;
}

export interface DataExtractionSummary {
  extracted: boolean;
  analytical_results_created: number;
  wells_created: number;
  plume_snapshot_created: boolean;
  site_updated: boolean;
  sample_date: string | null;
  errors: string[];
}

export interface DocumentUploadResponse {
  success: boolean;
  document: SiteDocument | null;
  message: string;
  auto_categorized?: boolean;
  categorization_confidence?: number | null;
  categorization_reason?: string | null;
  data_extraction?: DataExtractionSummary | null;
  sync_warning?: string | null;
}

export interface DataImportResponse {
  success: boolean;
  records_processed: number;
  records_created: number;
  records_updated: number;
  wells_created: number;
  errors: Array<{ row: number; error: string }>;
  warnings: string[];
}

export interface SiteImportResponse {
  success: boolean;
  sites_created: number;
  sites_updated: number;
  errors: Array<{ row: number; error: string }>;
  warnings: string[];
  created_site_ids: string[];
}

export interface DetectedSite {
  name: string;
  confidence: number;
  existing_site_id: string | null;
  existing_site_name: string | null;
  is_new: boolean;
  context: string | null;
  latitude: number | null;
  longitude: number | null;
  state: string | null;
  address: string | null;
}

export interface ExtractedMetadata {
  latitude: number | null;
  longitude: number | null;
  state: string | null;
  address: string | null;
  city: string | null;
}

export interface DocumentParseResponse {
  success: boolean;
  message: string;
  detected_sites: DetectedSite[];
  extracted_text_preview: string | null;
  suggestions: string[];
  extracted_metadata: ExtractedMetadata | null;
  // Auto-detected document fields
  detected_document_type: DocumentType | 'other' | null;
  detected_document_date: string | null;  // YYYY-MM-DD format
  document_type_confidence: number | null;
}

export interface DocumentFilters {
  document_type?: DocumentType;
  page?: number;
  page_size?: number;
}

// Human-readable labels for document types
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  agmr: 'AGMR',
  gw_gradient: 'GW Gradient',
  gw_concentrations: 'GW Concentrations',
  om: 'O&M',
  product_recovery: 'Product Recovery',
  lab_report: 'Lab Reports',
  other: 'Other',
};

// File size formatter
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export const documentsApi = {
  /**
   * List documents for a site
   */
  list: async (siteId: string, filters?: DocumentFilters): Promise<DocumentListResponse> => {
    const response = await apiClient.get<DocumentListResponse>(
      `/sites/${siteId}/documents`,
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get a single document's metadata
   */
  get: async (siteId: string, documentId: string): Promise<SiteDocument> => {
    const response = await apiClient.get<SiteDocument>(
      `/sites/${siteId}/documents/${documentId}`
    );
    return response.data;
  },

  /**
   * Upload a document
   * If documentType is 'auto' or not provided, Gemini AI will auto-categorize
   */
  upload: async (
    siteId: string,
    file: File,
    documentType?: DocumentType | 'auto',
    documentDate?: string,
    description?: string,
    folderId?: string,
    onProgress?: (progress: number) => void
  ): Promise<DocumentUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    // Only append document_type if it's not 'auto' - let backend auto-categorize
    if (documentType && documentType !== 'auto') {
      formData.append('document_type', documentType);
    }
    if (documentDate) {
      formData.append('document_date', documentDate);
    }
    if (description) {
      formData.append('description', description);
    }
    if (folderId) {
      formData.append('folder_id', folderId);
    }

    const response = await apiClient.post<DocumentUploadResponse>(
      `/sites/${siteId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const total = progressEvent.total || 1;
              const progress = Math.round((progressEvent.loaded * 100) / total);
              onProgress(progress);
            }
          : undefined,
      }
    );
    return response.data;
  },

  /**
   * Download a document (returns blob URL)
   */
  download: async (siteId: string, documentId: string, filename: string): Promise<void> => {
    const response = await apiClient.get(
      `/sites/${siteId}/documents/${documentId}/download`,
      { responseType: 'blob' }
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Get a viewable URL for a document (for inline preview)
   * Returns a blob URL that can be used in an iframe or img tag
   */
  getViewUrl: async (siteId: string, documentId: string): Promise<string> => {
    const response = await apiClient.get(
      `/sites/${siteId}/documents/${documentId}/view`,
      { responseType: 'blob' }
    );

    return window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
  },

  /**
   * Get a direct URL for streaming document view (no blob download).
   * Uses ?token= query param for auth so browsers/iframes can stream directly.
   * Much faster for large PDFs since the browser can render progressively.
   */
  getDirectViewUrl: (siteId: string, documentId: string): string => {
    const token = localStorage.getItem('argos-auth');
    let accessToken = '';
    try {
      const parsed = JSON.parse(token || '{}');
      accessToken = parsed?.state?.accessToken || '';
    } catch { /* */ }
    // Use relative URL so the request goes through the Vite dev proxy (or same origin in prod)
    return `/api/v1/sites/${siteId}/documents/${documentId}/view?token=${encodeURIComponent(accessToken)}`;
  },

  /**
   * Check if a document type is previewable
   */
  isPreviewable: (mimeType: string): boolean => {
    const previewableMimeTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/gif',
      'image/webp',
      'image/svg+xml',
      'text/plain',
      'text/html',
      'text/csv',
    ];
    return previewableMimeTypes.includes(mimeType);
  },

  /**
   * Delete a document
   */
  delete: async (siteId: string, documentId: string): Promise<void> => {
    await apiClient.delete(`/sites/${siteId}/documents/${documentId}`);
  },

  /**
   * Import groundwater monitoring data from Excel/CSV
   */
  importGroundwaterData: async (
    siteId: string,
    file: File,
    createWells: boolean = true
  ): Promise<DataImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<DataImportResponse>(
      `/sites/${siteId}/import/groundwater`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: { create_wells: createWells },
      }
    );
    return response.data;
  },

  /**
   * Import sites from Excel/CSV file
   * Creates new sites that will auto-populate into Polygon
   */
  importSites: async (file: File): Promise<SiteImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<SiteImportResponse>(
      '/sites/import/sites',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Parse a document to detect site names
   * Uses text extraction and fuzzy matching to find existing sites
   * or suggest new site names based on document content
   */
  parseDocument: async (file: File): Promise<DocumentParseResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<DocumentParseResponse>(
      '/sites/parse-document',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // ============ FOLDER OPERATIONS ============

  /**
   * List all folders for a site in tree structure
   */
  listFolders: async (siteId: string): Promise<FolderTreeResponse> => {
    const response = await apiClient.get<FolderTreeResponse>(
      `/sites/${siteId}/folders`
    );
    return response.data;
  },

  /**
   * Create a new folder
   */
  createFolder: async (
    siteId: string,
    data: FolderCreateRequest
  ): Promise<DocumentFolder> => {
    const response = await apiClient.post<DocumentFolder>(
      `/sites/${siteId}/folders`,
      data
    );
    return response.data;
  },

  /**
   * Get folder details
   */
  getFolder: async (siteId: string, folderId: string): Promise<DocumentFolder> => {
    const response = await apiClient.get<DocumentFolder>(
      `/sites/${siteId}/folders/${folderId}`
    );
    return response.data;
  },

  /**
   * Update a folder (rename or move)
   */
  updateFolder: async (
    siteId: string,
    folderId: string,
    data: FolderUpdateRequest
  ): Promise<DocumentFolder> => {
    const response = await apiClient.put<DocumentFolder>(
      `/sites/${siteId}/folders/${folderId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a folder
   * @param deleteContents - If true, delete all documents in folder. If false, move to root.
   */
  deleteFolder: async (
    siteId: string,
    folderId: string,
    deleteContents: boolean = false
  ): Promise<void> => {
    await apiClient.delete(`/sites/${siteId}/folders/${folderId}`, {
      params: { delete_contents: deleteContents },
    });
  },

  /**
   * Move a document to a different folder
   */
  moveDocument: async (
    siteId: string,
    documentId: string,
    folderId: string | null
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put<{ success: boolean; message: string }>(
      `/sites/${siteId}/documents/${documentId}/move`,
      { folder_id: folderId }
    );
    return response.data;
  },

  /**
   * Check for duplicate files by content hash
   */
  checkDuplicates: async (
    siteId: string,
    contentHashes: string[]
  ): Promise<DuplicateCheckResponse> => {
    const response = await apiClient.post<DuplicateCheckResponse>(
      `/sites/${siteId}/documents/check-duplicates`,
      { content_hashes: contentHashes }
    );
    return response.data;
  },

  // =========================================================================
  // Sync Status Functions
  // =========================================================================

  /**
   * Get document sync status summary
   */
  getSyncStatus: async (): Promise<SyncStatusResponse> => {
    const response = await apiClient.get<SyncStatusResponse>('/sites/-/sync-status');
    return response.data;
  },

  /**
   * Get sync alerts (unacknowledged by default)
   */
  getSyncAlerts: async (acknowledged?: boolean): Promise<SyncAlertsResponse> => {
    const response = await apiClient.get<SyncAlertsResponse>('/sites/-/sync-alerts', {
      params: { acknowledged },
    });
    return response.data;
  },

  /**
   * Acknowledge sync alerts
   */
  acknowledgeSyncAlerts: async (alertIds?: string[]): Promise<{ acknowledged: number; message: string }> => {
    const response = await apiClient.post<{ acknowledged: number; message: string }>(
      '/sites/-/sync-alerts/acknowledge',
      { alert_ids: alertIds }
    );
    return response.data;
  },

  /**
   * Retry sync for a specific document
   */
  retrySyncDocument: async (documentId: string): Promise<{ message: string; task_id: string }> => {
    const response = await apiClient.post<{ message: string; task_id: string }>(
      `/sites/documents/${documentId}/retry-sync`
    );
    return response.data;
  },

  // =========================================================================
  // Filter Options
  // =========================================================================

  /**
   * Get filter options for document filtering
   * Returns unique states, consulting companies, sites, and years
   */
  getFilterOptions: async (): Promise<DocumentFilterOptionsResponse> => {
    const response = await apiClient.get<DocumentFilterOptionsResponse>('/sites/-/filter-options');
    return response.data;
  },

  /**
   * Get all documents across all sites with optional filtering
   */
  getAllDocuments: async (filters?: {
    state?: string;
    company?: string;
    site_id?: string;
    year?: number;
    document_type?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ items: SiteDocument[]; total: number; page: number; page_size: number }> => {
    const params = new URLSearchParams();
    if (filters?.state) params.append('state', filters.state);
    if (filters?.company) params.append('company', filters.company);
    if (filters?.site_id) params.append('site_id', filters.site_id);
    if (filters?.year) params.append('year', String(filters.year));
    if (filters?.document_type) params.append('document_type', filters.document_type);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.page_size) params.append('page_size', String(filters.page_size));

    const queryString = params.toString();
    const url = `/sites/-/documents${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // =========================================================================
  // Bulk Upload Operations
  // =========================================================================

  /**
   * Upload multiple files at once with parallel processing
   * Files are queued for AI categorization and optional extraction
   */
  bulkUpload: async (
    siteId: string,
    files: File[],
    options?: BulkUploadOptions
  ): Promise<BulkUploadResponse> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    if (options?.auto_categorize !== undefined) {
      formData.append('auto_categorize', String(options.auto_categorize));
    }
    if (options?.auto_extract !== undefined) {
      formData.append('auto_extract', String(options.auto_extract));
    }

    const response = await apiClient.post<BulkUploadResponse>(
      `/sites/${siteId}/documents/bulk-upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },

  /**
   * Get status of a bulk upload batch
   * Poll this endpoint to track upload progress
   */
  getBulkUploadStatus: async (
    siteId: string,
    batchId: string
  ): Promise<BulkUploadStatusResponse> => {
    const response = await apiClient.get<BulkUploadStatusResponse>(
      `/sites/${siteId}/documents/bulk-status/${batchId}`
    );
    return response.data;
  },

  // =========================================================================
  // Bulk Extraction Operations
  // =========================================================================

  /**
   * Queue multiple documents for extraction
   * If no document IDs provided, extracts all pending documents
   */
  bulkExtract: async (
    siteId: string,
    documentIds?: string[]
  ): Promise<BulkExtractResponse> => {
    const response = await apiClient.post<BulkExtractResponse>(
      `/sites/${siteId}/documents/bulk-extract`,
      { document_ids: documentIds || [] }
    );
    return response.data;
  },

  /**
   * Get status of a bulk extraction batch
   * Poll this endpoint to track extraction progress
   */
  getExtractionStatus: async (
    siteId: string,
    batchId: string
  ): Promise<ExtractionStatusResponse> => {
    const response = await apiClient.get<ExtractionStatusResponse>(
      `/sites/${siteId}/documents/extraction-status/${batchId}`
    );
    return response.data;
  },

  // =========================================================================
  // Batch Actions (Multi-Select)
  // =========================================================================

  /**
   * Delete multiple documents at once
   */
  batchDelete: async (
    siteId: string,
    documentIds: string[]
  ): Promise<BatchResult> => {
    const response = await apiClient.post<BatchResult>(
      `/sites/${siteId}/documents/batch-delete`,
      { document_ids: documentIds }
    );
    return response.data;
  },

  /**
   * Download multiple documents as a ZIP file
   */
  batchDownload: async (
    siteId: string,
    documentIds: string[]
  ): Promise<Blob> => {
    const response = await apiClient.post(
      `/sites/${siteId}/documents/batch-download`,
      { document_ids: documentIds },
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Move multiple documents to a different site
   */
  batchMove: async (
    siteId: string,
    documentIds: string[],
    targetSiteId: string
  ): Promise<BatchResult> => {
    const response = await apiClient.post<BatchResult>(
      `/sites/${siteId}/documents/batch-move`,
      { document_ids: documentIds, target_site_id: targetSiteId }
    );
    return response.data;
  },

  /**
   * Retry extraction for multiple failed documents
   */
  batchRetryExtraction: async (
    siteId: string,
    documentIds: string[]
  ): Promise<BulkExtractResponse> => {
    const response = await apiClient.post<BulkExtractResponse>(
      `/sites/${siteId}/documents/batch-retry-extraction`,
      { document_ids: documentIds }
    );
    return response.data;
  },
};

// Vault filter options types
export interface SiteFilterOption {
  id: string;
  name: string;
  state: string | null;
  consulting_company: string | null;
}

export interface DocumentFilterOptionsResponse {
  states: string[];
  companies: string[];
  sites: SiteFilterOption[];
  years: number[];
}

// =========================================================================
// Bulk Upload Types
// =========================================================================

export interface BulkUploadOptions {
  auto_categorize?: boolean;
  auto_extract?: boolean;
}

export interface BulkUploadResponse {
  batch_id: string;
  total_files: number;
  message: string;
}

export interface BulkUploadFileStatus {
  filename: string;
  status: 'queued' | 'uploading' | 'categorizing' | 'completed' | 'failed';
  document_id: string | null;
  document_type: string | null;
  categorization_confidence: number | null;
  extraction_status: string | null;
  error: string | null;
}

export interface BulkUploadStatusResponse {
  batch_id: string;
  total: number;
  completed: number;
  failed: number;
  processing: number;
  files: BulkUploadFileStatus[];
}

// =========================================================================
// Bulk Extraction Types
// =========================================================================

export interface BulkExtractResponse {
  batch_id: string;
  total: number;
  queued: number;
  skipped: number;
  message: string;
}

export interface ExtractionDocumentStatus {
  id: string;
  filename: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  progress_percent: number;
  error: string | null;
  extracted_fields: number | null;
  confidence: number | null;
}

export interface ExtractionStatusResponse {
  batch_id: string;
  total: number;
  completed: number;
  failed: number;
  processing: number;
  skipped: number;
  documents: ExtractionDocumentStatus[];
}

// =========================================================================
// Batch Actions Types
// =========================================================================

export interface BatchResult {
  success: boolean;
  total: number;
  processed: number;
  failed: number;
  errors: Array<{ id: string; error: string }>;
}

// Sync status types
export interface SyncStatusResponse {
  pending_count: number;
  failed_count: number;
  synced_count: number;
  total_count: number;
  unacknowledged_alerts: number;
  cloud_available: boolean;
}

export interface SyncAlert {
  id: string;
  document_id: string;
  document_name: string;
  alert_type: string;
  message: string | null;
  created_at: string;
  acknowledged: boolean;
}

export interface SyncAlertsResponse {
  alerts: SyncAlert[];
  total: number;
}

// =========================================================================
// Extraction Types
// =========================================================================

// Table preview for shapefile/GIS extraction
export interface TablePreview {
  columns: string[];
  rows: Record<string, unknown>[];
}

// Well location from extraction
export interface ExtractedWellLocation {
  well_id: string;
  latitude: number;
  longitude: number;
  ground_elevation_ft?: number | null;
  total_depth_ft?: number | null;
  screen_top_ft?: number | null;
  screen_bottom_ft?: number | null;
  casing_material?: string | null;
  confidence: number;
}

// Analytical result from extraction (uses new field names: compound, concentration)
export interface ExtractedWellSample {
  well_id: string;
  compound: string;  // renamed from 'parameter'
  concentration?: number | null;  // renamed from 'result_value'
  units: string;
  sample_date?: string | null;
  is_non_detect: boolean;  // was 'is_censored'
  detection_limit?: number | null;
  lab_method?: string | null;
  days_since_spill?: number | null;
  confidence: number;
}

// Plume boundary from extraction
export interface ExtractedPlumeBoundary {
  boundary_geojson?: unknown;
  centroid_latitude?: number | null;
  centroid_longitude?: number | null;
  plume_area_sqft?: number | null;
  confidence: number;
}

// Full extraction data structure (unified schema)
export interface ExtractionData {
  well_samples?: ExtractedWellSample[];
  well_locations?: ExtractedWellLocation[];
  hydrogeology?: Record<string, unknown>;
  source_characteristics?: Record<string, unknown>;
  plume_geometry?: ExtractedPlumeBoundary;
  document_metadata?: {
    source_filename?: string;
    extraction_method?: string;
    geometry_type?: string;  // For GIS: Point, Polygon, etc.
    crs_detected?: string;   // For GIS: EPSG:4326, etc.
    feature_count?: number;
    columns?: string[];
    column_mapping?: Record<string, string>;
  };
  table_preview?: TablePreview;  // For GIS: raw spreadsheet view
}



